/**
 * Floating format toolbar (bubble menu): appears above selected
 * text. Positioning is handled by @tiptap/extension-bubble-menu;
 * this component provides the element and buttons only.
 */
import { createEffect, createSignal, For, onCleanup, Show } from "solid-js";
import type { Editor } from "@tiptap/core";
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Italic,
  List,
  MessageSquarePlus,
  Scissors,
  Strikethrough,
} from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { cn } from "@folio/shared";
import { useI18n } from "@/providers/i18n-provider";
import { suggestDeleteRange } from "@/features/comments/lib/suggestion-mode";

interface SelectionToolbarProps {
  editor: () => Editor | null;
  setElement: (element: HTMLDivElement) => void;
  /** Comment selection (opens panel composer). */
  onAddComment?: () => void;
  /** Suggestion mode state (shows "suggest deletion"). */
  suggestionMode?: () => boolean;
  /** New deletion proposal created from toolbar. */
  onSuggestDelete?: (suggestionId: string, text: string) => void;
}

export function SelectionToolbar(props: SelectionToolbarProps) {
  const { t } = useI18n();
  const [version, setVersion] = createSignal(0);

  // Re-evaluate isActive() on each editor transaction
  createEffect(() => {
    const editor = props.editor();
    if (!editor) {
      return;
    }
    const bump = () => setVersion((value) => value + 1);
    editor.on("transaction", bump);
    onCleanup(() => {
      editor.off("transaction", bump);
    });
  });

  const isActive = (name: string, attrs?: Record<string, unknown>) => {
    version();
    return props.editor()?.isActive(name, attrs) ?? false;
  };

  const buttons = () => [
    {
      id: "bold",
      label: t(msg`Bold`),
      icon: Bold,
      active: isActive("bold"),
      run: () => props.editor()?.chain().focus().toggleBold().run(),
    },
    {
      id: "italic",
      label: t(msg`Italic`),
      icon: Italic,
      active: isActive("italic"),
      run: () => props.editor()?.chain().focus().toggleItalic().run(),
    },
    {
      id: "strike",
      label: t(msg`Strikethrough`),
      icon: Strikethrough,
      active: isActive("strike"),
      run: () => props.editor()?.chain().focus().toggleStrike().run(),
    },
    {
      id: "code",
      label: t(msg`Inline code`),
      icon: Code,
      active: isActive("code"),
      run: () => props.editor()?.chain().focus().toggleCode().run(),
    },
    {
      id: "h1",
      label: t(msg`Heading 1`),
      icon: Heading1,
      active: isActive("heading", { level: 1 }),
      run: () => props.editor()?.chain().focus().toggleHeading({ level: 1 }).run(),
    },
    {
      id: "h2",
      label: t(msg`Heading 2`),
      icon: Heading2,
      active: isActive("heading", { level: 2 }),
      run: () => props.editor()?.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      id: "bulletList",
      label: t(msg`Bullet list`),
      icon: List,
      active: isActive("bulletList"),
      run: () => props.editor()?.chain().focus().toggleBulletList().run(),
    },
  ];

  return (
    <div
      ref={props.setElement}
      class="selection-toolbar"
      style={{ visibility: "hidden", position: "absolute" }}
    >
      <For each={buttons()}>
        {(button) => (
          <button
            type="button"
            class={cn("selection-toolbar-button", button.active && "is-active")}
            onMouseDown={(e) => {
              // Do not steal focus from selection
              e.preventDefault();
              button.run();
            }}
            aria-label={button.label}
            title={button.label}
          >
            <button.icon class="h-4 w-4" />
          </button>
        )}
      </For>
      <Show when={props.onAddComment}>
        <button
          type="button"
          class="selection-toolbar-button"
          data-testid="selection-comment"
          onMouseDown={(e) => {
            e.preventDefault();
            props.onAddComment?.();
          }}
          aria-label={t(msg`Comment`)}
          title={t(msg`Comment`)}
        >
          <MessageSquarePlus class="h-4 w-4" />
        </button>
      </Show>
      <Show when={props.suggestionMode?.() && props.onSuggestDelete}>
        <button
          type="button"
          class="selection-toolbar-button"
          data-testid="selection-suggest-delete"
          onMouseDown={(e) => {
            e.preventDefault();
            const editor = props.editor();
            if (!editor) {
              return;
            }
            const { from, to } = editor.state.selection;
            suggestDeleteRange(editor, from, to, props.onSuggestDelete);
          }}
          aria-label={t(msg`Suggest deletion`)}
          title={t(msg`Suggest deletion`)}
        >
          <Scissors class="h-4 w-4" />
        </button>
      </Show>
    </div>
  );
}
